import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { trackPageView } from "./utils/analytics";
import ScrollToTop from "./components/ScrollToTop";

// Redirect Components for geographical directories to point to religion profiles
const StateRedirect = () => {
  const { state } = useParams();
  return <Navigate to={`/religion/${state}`} replace />;
};

const DistrictRedirect = () => {
  const { state, district } = useParams();
  return <Navigate to={`/religion/${state}/${district}`} replace />;
};

const SubdistrictRedirect = () => {
  const { state, district, slug } = useParams();
  return <Navigate to={`/religion/${state}/${district}/${slug}`} replace />;
};

const VillageRedirect = () => {
  const { state, district, subdistrict } = useParams();
  return <Navigate to={`/religion/${state}/${district}/${subdistrict}`} replace />;
};

// Lazy load components
const HomePage = lazy(() => import("./components/HomePage"));
const DistrictReligionPage = lazy(() => import("./components/DistrictReligionPage"));
const AboutData = lazy(() => import("./pages/AboutData"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const DynamicSlugPage = lazy(() => import("./components/DynamicSlugPage")); // eslint-disable-line no-unused-vars
const ReligionDataPage = lazy(() => import("./components/ReligionDataPage"));
const TopReligionStatesPage = lazy(() => import("./components/TopReligionStatesPage"));
const BottomReligionStatesPage = lazy(() => import("./components/BottomReligionStatesPage"));
const TopReligionDistrictsPage = lazy(() => import("./components/TopReligionDistrictsPage"));
const BottomReligionDistrictsPage = lazy(() => import("./components/BottomReligionDistrictsPage"));
const ReligionAnalysisPage = lazy(() => import("./components/ReligionAnalysisPage"));
const IndiaReligionPage = lazy(() => import("./components/IndiaReligionPage"));
const IndiaGenderAnalysisPage = lazy(() => import("./components/IndiaGenderAnalysisPage"));
const IndiaUrbanRuralPage = lazy(() => import("./components/IndiaUrbanRuralPage"));
const DiversityAnalysisPage = lazy(() => import("./components/DiversityAnalysisPage"));
const GenderGapAnalysisPage = lazy(() => import("./components/GenderGapAnalysisPage"));
const LanguageAnalysisPage = lazy(() => import("./components/LanguageAnalysisPage"));
const SubdistrictReligionPage = lazy(() => import("./components/SubdistrictReligionPage"));
const StateReligionPage = lazy(() => import("./components/StateReligionPage"));
const Census2027Page = lazy(() => import("./components/Census2027Page"));
const CensusSelfEnumerationGuidePage = lazy(() => import("./components/CensusSelfEnumerationGuidePage"));
const CensusHousingQuestionsPage = lazy(() => import("./components/CensusHousingQuestionsPage"));
const CensusRequiredDetailsPage = lazy(() => import("./components/CensusRequiredDetailsPage"));
const CensusErrorsFaqPage = lazy(() => import("./components/CensusErrorsFaqPage"));
const DataCorrectionPage = lazy(() => import("./pages/DataCorrection"));


const Loading = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

function App() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return (
    <div className="app-container">
      <ScrollToTop />
      <Header />
      <main className="main-content">
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about-us" element={<AboutData />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/data-correction" element={<DataCorrectionPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/sitemap" element={<SitemapPage />} />

            <Route
              path="/religion"
              element={<ReligionDataPage />}
            />

            <Route
              path="/religion/:state"
              element={<StateReligionPage />}
            />

            <Route
              path="/religion/:stateSlug/:districtSlug"
              element={<DistrictReligionPage />}
            />

            <Route
              path="/religion/:stateSlug/:districtSlug/:subdistrictSlug"
              element={<SubdistrictReligionPage />}
            />

            <Route
              path="/rankings/religion/:religion"
              element={<TopReligionStatesPage />}
            />

            <Route
              path="/rankings/minority/:religion"
              element={<BottomReligionStatesPage />}
            />

            <Route
              path="/analysis/:topic"
              element={<ReligionAnalysisPage />}
            />

            <Route
              path="/rankings/district/majority/:religion"
              element={<TopReligionDistrictsPage />}
            />

            <Route
              path="/rankings/district/minority/:religion"
              element={<BottomReligionDistrictsPage />}
            />

            <Route
              path="/india-census-2011"
              element={<IndiaReligionPage />}
            />

            <Route
              path="/analysis/religion-gender-ratio"
              element={<IndiaGenderAnalysisPage />}
            />

            <Route
              path="/analysis/religion-urban-rural"
              element={<IndiaUrbanRuralPage />}
            />

            <Route
              path="/analysis/diversity-index"
              element={<DiversityAnalysisPage />}
            />

            <Route
              path="/analysis/critical-gender-gaps"
              element={<GenderGapAnalysisPage />}
            />

            <Route
              path="/analysis/language-census-2011"
              element={<LanguageAnalysisPage />}
            />

            <Route
              path="/census-2027-upcoming"
              element={<Census2027Page />}
            />
            <Route
              path="/census-2027-upcoming/self-enumeration-guide"
              element={<CensusSelfEnumerationGuidePage />}
            />
            <Route
              path="/census-2027-upcoming/housing-census-questions"
              element={<CensusHousingQuestionsPage />}
            />
            <Route
              path="/census-2027-upcoming/required-details"
              element={<CensusRequiredDetailsPage />}
            />
            <Route
              path="/census-2027-upcoming/errors-faq"
              element={<CensusErrorsFaqPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming"
              element={<Census2027Page />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/self-enumeration-guide"
              element={<CensusSelfEnumerationGuidePage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/housing-census-questions"
              element={<CensusHousingQuestionsPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/required-details"
              element={<CensusRequiredDetailsPage />}
            />
            <Route
              path="/:langCode/census-2027-upcoming/errors-faq"
              element={<CensusErrorsFaqPage />}
            />

            {/* Redirect geographical directory pages to their religion counterparts */}
            <Route
              path="/:state/:district/:subdistrict/:village"
              element={<VillageRedirect />}
            />
            <Route path="/:state" element={<StateRedirect />} />
            <Route path="/:state/:district" element={<DistrictRedirect />} />
            <Route
              path="/:state/:district/:slug"
              element={<SubdistrictRedirect />}
            />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;
